import { Button, Field, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';

import { PropertyMultiSelect } from '../../../components/form/property-multi-select.js';
import { generatePassword } from '../../../lib/generate-password.js';
import {
  isSystemRole,
  type PropertyOption,
  type RoleOption,
  type UserFormInput,
} from '../api/schemas.js';

export interface UserFieldsProps {
  readonly register: UseFormRegister<UserFormInput>;
  readonly errors: FieldErrors<UserFormInput>;
  readonly control: Control<UserFormInput>;
  readonly setValue: UseFormSetValue<UserFormInput>;
  readonly mode: 'create' | 'edit';
  readonly roleOptions: readonly RoleOption[];
  readonly propertyOptions: readonly PropertyOption[];
}

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
] as const;

export function UserFields({
  register,
  errors,
  control,
  setValue,
  mode,
  roleOptions,
  propertyOptions,
}: UserFieldsProps): React.JSX.Element {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="space-y-6">
      <Section title="Identity" description="The account's email, name and access status.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field error={errors.email?.message} required={mode === 'create'}>
            <FieldLabel>Email</FieldLabel>
            <FieldInput
              autoComplete="off"
              type="email"
              readOnly={mode === 'edit'}
              {...register('email')}
            />
          </Field>

          {mode === 'create' ? (
            <Field error={errors.password?.message} required>
              <FieldLabel>Initial password</FieldLabel>
              <div className="flex items-center gap-2">
                <FieldInput
                  className="flex-1"
                  type={revealed ? 'text' : 'password'}
                  autoComplete="new-password"
                  {...register('password')}
                />
                <Button
                  type="button"
                  variant="outline"
                  aria-label={revealed ? 'Hide password' : 'Show password'}
                  onClick={() => {
                    setRevealed((previous) => !previous);
                  }}
                >
                  {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setValue('password', generatePassword(), {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setRevealed(true);
                  }}
                >
                  <RefreshCw className="h-4 w-4" /> Generate
                </Button>
              </div>
            </Field>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field error={errors.firstName?.message}>
            <FieldLabel>First name</FieldLabel>
            <FieldInput autoComplete="off" {...register('firstName')} />
          </Field>

          <Field error={errors.lastName?.message}>
            <FieldLabel>Last name</FieldLabel>
            <FieldInput autoComplete="off" {...register('lastName')} />
          </Field>
        </div>

        <Field error={errors.status?.message}>
          <FieldLabel>Status</FieldLabel>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <FieldSelect
                value={field.value ?? ''}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
                options={[...STATUS_OPTIONS]}
                placeholder="Select a status"
                searchable
              />
            )}
          />
        </Field>
      </Section>

      <Section
        title="Role"
        description="Only one role can be assigned to a user. Only the roles you can see can be selected."
      >
        <Controller
          control={control}
          name="roleIds"
          render={({ field }) => (
            <Field error={errors.roleIds?.message}>
              <FieldLabel>Role</FieldLabel>
              <FieldSelect
                value={field.value[0] ?? ''}
                onValueChange={(value) => {
                  field.onChange(value === '' ? [] : [value]);
                }}
                onBlur={field.onBlur}
                options={roleOptions
                  .filter((role) => !isSystemRole(role))
                  .map((role) => ({ value: role.id, label: role.name }))}
                placeholder="Select a role"
              />
            </Field>
          )}
        />
      </Section>

      <Section
        title="Properties"
        description="When a role is property-scoped, these are the properties the account can reach."
      >
        <Controller
          control={control}
          name="propertyIds"
          render={({ field }) => (
            <PropertyMultiSelect
              value={field.value}
              onChange={(value) => {
                field.onChange([...value]);
              }}
              error={errors.propertyIds?.message}
              label=""
              description=""
              options={propertyOptions}
            />
          )}
        />
      </Section>
    </div>
  );
}
